// AI Chat Bot - Server Backend mit Sessions und Produktkarten
const SHIPPING_FAQ = Object.freeze({
  domesticFreeFrom: 39.0,
  domesticShippingUnder: 3.99,
  euShipping: 13.99,
  fsk18ShippingUnder: 6.99,
  fsk18CheckFeeFrom: 2.99
});

const EU_COUNTRY_TERMS = [
  "ausland",
  "österreich", "oesterreich", "austria", "frankreich", "france", "italien", "italy",
  "spanien", "spain", "niederlande", "holland", "belgien", "belgium", "luxemburg",
  "polen", "poland", "tschechien", "czech", "dänemark", "daenemark", "denmark",
  "schweden", "sweden", "finnland", "finland", "portugal", "irland", "ireland",
  "griechenland", "greece", "rumänien", "rumaenien", "romania", "bulgarien", "croatia",
  "kroatien", "slowakei", "slovakia", "slowenien", "slovenia", "ungarn", "hungary",
  "estland", "estonia", "lettland", "latvia", "litauen", "lithuania", "zypern", "cyprus",
  "malta"
];

function normalizeShippingText(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function messageIncludesAny(text, terms) {
  return terms.some(term => text.includes(term));
}

function extractOrderValueEUR(rawText) {
  const text = String(rawText || "");
  const euroMatches = [...text.matchAll(/(\d{1,4}(?:[.,]\d{1,2})?)\s*(?:€|eur|euro)/gi)];
  if (euroMatches.length) {
    const value = euroMatches[0][1].replace(",", ".");
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }

  const genericMoneyMatches = [...text.matchAll(/\b(\d{1,4}(?:[.,]\d{1,2})?)\b/g)];
  for (const m of genericMoneyMatches) {
    const num = Number(String(m[1]).replace(",", "."));
    if (Number.isFinite(num) && num >= 10 && num <= 5000) {
      return num;
    }
  }

  return null;
}

function buildDomesticShippingReply(orderValue) {
  if (orderValue == null) {
    return [
      "Innerhalb Deutschlands versenden wir mit DHL.",
      `Ab ${SHIPPING_FAQ.domesticFreeFrom.toFixed(2).replace(".", ",")} € ist der Versand kostenfrei.`,
      `Darunter liegen die Versandkosten bei ${SHIPPING_FAQ.domesticShippingUnder.toFixed(2).replace(".", ",")} € pro Bestellung.`,
      "Die genaue Lieferzeit siehst du immer direkt auf der Produktseite."
    ].join(" ");
  }

  if (orderValue >= SHIPPING_FAQ.domesticFreeFrom) {
    return [
      `Bei einem Bestellwert von ${orderValue.toFixed(2).replace(".", ",")} € ist der Versand innerhalb Deutschlands kostenfrei.`,
      "Versandart: DHL.",
      "Die genaue Lieferzeit steht beim jeweiligen Artikel auf der Produktseite."
    ].join(" ");
  }

  return [
    `Bei einem Bestellwert von ${orderValue.toFixed(2).replace(".", ",")} € fallen innerhalb Deutschlands ${SHIPPING_FAQ.domesticShippingUnder.toFixed(2).replace(".", ",")} € Versandkosten an.`,
    `Ab ${SHIPPING_FAQ.domesticFreeFrom.toFixed(2).replace(".", ",")} € liefern wir versandkostenfrei.`,
    "Versandart: DHL."
  ].join(" ");
}

function buildFsk18DomesticReply(orderValue) {
  const restrictions = "Bitte beachten: Keine Lieferung an Packstationen, keine Zustellung an Nachbarn oder Hausbewohner. Zustellung nur persönlich an den volljährigen Empfänger mit gültigem Lichtbildausweis.";

  if (orderValue == null) {
    return [
      "FSK18-Artikel (bzw. Artikel ohne Jugendfreigabe) versenden wir innerhalb Deutschlands ausschließlich mit DHL und Alterssichtprüfung.",
      `Unter ${SHIPPING_FAQ.domesticFreeFrom.toFixed(2).replace(".", ",")} € Bestellwert betragen die Versandkosten ${SHIPPING_FAQ.fsk18ShippingUnder.toFixed(2).replace(".", ",")} €.`,
      `Ab ${SHIPPING_FAQ.domesticFreeFrom.toFixed(2).replace(".", ",")} € fällt nur ein Altersprüfungs-Aufpreis von ${SHIPPING_FAQ.fsk18CheckFeeFrom.toFixed(2).replace(".", ",")} € an.`,
      restrictions
    ].join(" ");
  }

  if (orderValue >= SHIPPING_FAQ.domesticFreeFrom) {
    return [
      `Bei ${orderValue.toFixed(2).replace(".", ",")} € Bestellwert zahlst du für FSK18-Artikel nur den Altersprüfungs-Aufpreis von ${SHIPPING_FAQ.fsk18CheckFeeFrom.toFixed(2).replace(".", ",")} €.`,
      "Der Versand erfolgt mit DHL und Alterssichtprüfung.",
      restrictions
    ].join(" ");
  }

  return [
    `Bei ${orderValue.toFixed(2).replace(".", ",")} € Bestellwert betragen die Versandkosten für FSK18-Artikel ${SHIPPING_FAQ.fsk18ShippingUnder.toFixed(2).replace(".", ",")} €.`,
    "Der Versand erfolgt mit DHL und Alterssichtprüfung.",
    restrictions
  ].join(" ");
}

function getShippingFaqReply(rawMessage) {
  const text = normalizeShippingText(rawMessage);
  if (!text) return null;

  const asksWhy57 = /(warum|wieso|weshalb).*(5[\s-]?7).*(werktag|tage)|5[\s-]?7.*(warum|wieso|weshalb)/i.test(text);
  const shippingSignals = [
    "versand", "versend", "lieferzeit", "lieferdauer", "lieferung", "liefern", "liefert",
    "zustellung", "sendung", "tracking", "sendungsverfolgung",
    "versandkosten", "lieferkosten", "porto", "packstation", "fsk18", "fsk 18",
    "jugendfreigabe", "dhl", "deutsche post", "ausland", "werktag", "paket", "dienstleister"
  ];
  if (!asksWhy57 && !messageIncludesAny(text, shippingSignals)) return null;

  const isFsk18 = /fsk\s*18|fsk18|ohne jugendfreigabe|ab\s*18|18\+/i.test(text);
  const mentionsGermany = /innerhalb deutschland|nur deutschland|deutschlandweit|in deutschland|nach deutschland|deutschland/i.test(text);
  const isEuKeyword = /\beu\b|eu[-\s]?staat|eu[-\s]?(länder|laender)|international/i.test(text);
  const mentionsEuOrAbroad = isEuKeyword || messageIncludesAny(text, EU_COUNTRY_TERMS);
  const isEuOrAbroad = mentionsEuOrAbroad && !mentionsGermany;
  const asksTracking = /tracking|sendungsverfolg|versandbestätigung|versandbestaetigung|wo.*paket|paketstatus/i.test(text);
  const asksRestrictions = /packstation|nachbar|hausbewohner|ausweis|alterssicht|identit/i.test(text);
  const asksCosts = /versandkosten|lieferkosten|porto|kosten|wie teuer|preis/i.test(text);
  const asksMethod = /dhl|deutsche post|versandart|versendet ihr|womit/i.test(text);
  const orderValue = extractOrderValueEUR(rawMessage);

  if (asksWhy57) {
    return [
      "Die Lieferzeit von 5–7 Werktagen entsteht bei manchen Titeln durch die Beschaffung über unsere Verlagspartner.",
      "Wir führen ein sehr großes Sortiment an Büchern und Filmen, darunter auch seltene Ausgaben, ältere Titel und spezielle Editionen.",
      "Sobald der Artikel bei uns eingetroffen ist, wird er direkt an dich weitergeschickt."
    ].join(" ");
  }

  if (asksTracking) {
    return "Nach dem Versand erhältst du automatisch eine Versandbestätigung per E-Mail. Wenn verfügbar, ist darin auch der Link zur Sendungsverfolgung enthalten.";
  }

  if (isFsk18 && isEuOrAbroad) {
    return "FSK18-Artikel und Artikel ohne Jugendfreigabe können nicht ins Ausland geliefert werden. Diese Artikel versenden wir aus Jugendschutzgründen nur innerhalb Deutschlands mit DHL und Alterssichtprüfung.";
  }

  if (isFsk18 || asksRestrictions) {
    return buildFsk18DomesticReply(orderValue);
  }

  if (isEuOrAbroad) {
    return [
      "In ausgewählte EU-Staaten versenden wir per DHL.",
      "Die Lieferzeit liegt in der Regel bei 7 bis 21 Werktagen.",
      `Die Versandkosten betragen ${SHIPPING_FAQ.euShipping.toFixed(2).replace(".", ",")} € pro Bestellung (unabhängig vom Bestellwert).`,
      "FSK18-Artikel und Artikel ohne Jugendfreigabe sind vom Auslandsversand ausgeschlossen."
    ].join(" ");
  }

  if (asksMethod && mentionsGermany && mentionsEuOrAbroad) {
    return "Innerhalb Deutschlands versenden wir mit DHL. In ausgewählte EU-Staaten erfolgt der Versand per DHL.";
  }

  if (asksMethod && !asksCosts) {
    return "Wir versenden zuverlässig mit DHL. Die genaue Lieferzeit siehst du immer direkt auf der jeweiligen Produktseite.";
  }

  if (asksCosts || /deutschland|innerhalb deutschland|in deutschland/i.test(text)) {
    return buildDomesticShippingReply(orderValue);
  }

  return [
    "Wir versenden deine Bestellung mit DHL.",
    "Innerhalb Deutschlands ist der Versand ab 39,00 € kostenfrei, darunter 3,99 € pro Bestellung.",
    "In ausgewählte EU-Staaten beträgt der Versand 13,99 € bei 7–21 Werktagen Lieferzeit.",
    "FSK18-Artikel versenden wir nur innerhalb Deutschlands mit Alterssichtprüfung."
  ].join(" ");
}

if (typeof module !== "undefined" && module.exports) {
  module.exports = { getShippingFaqReply, extractOrderValueEUR };
}

(function() {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (window.__aiChatLoaded) return;
  window.__aiChatLoaded = true;

  const chatBox = document.getElementById('ai-chat-box');
  const chatTrigger = document.getElementById('ai-chat-trigger');
  const chatTriggerMobile = document.getElementById('ai-chat-trigger-mobile');
  const chatClose = document.getElementById('ai-chat-close');
  const messagesContainer = document.getElementById('ai-chat-messages');
  const inputField = document.getElementById('ai-chat-input');
  const sendButton = document.getElementById('ai-chat-send');

  let CUSTOMER_ID;
  let CURRENT_SESSION_ID = null;

  if (window.cinebuchCustomer?.id) {
    CUSTOMER_ID = window.cinebuchCustomer.id;
  } else {
    let guestId = localStorage.getItem('cinebuch_gast_id');
    if (!guestId) {
      guestId = 'guest_' + Date.now();
      localStorage.setItem('cinebuch_gast_id', guestId);
    }
    CUSTOMER_ID = guestId;
  }

  const API_BASE = 'https://api.cinebuch.de/api';
  const PRODUCT_CACHE_KEY = 'cinebuch_chat_product_cache_v1';
  const PENDING_SESSION_KEY = '__pending__';
  const MAX_CACHED_PRODUCTS_PER_SESSION = 40;
  const CHAT_SESSION_KEY = 'cinebuch_chat_session_id';
  const CHAT_LAST_ACTIVITY_KEY = 'cinebuch_chat_last_activity';
  // Sichtbarer Verlauf endet nach 30 Minuten Inaktivitaet (Server-Historie bleibt erhalten).
  const CHAT_HISTORY_TTL_MS = 30 * 60 * 1000;

  function readProductCache() {
    try {
      const raw = localStorage.getItem(PRODUCT_CACHE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (_) {
      return {};
    }
  }

  function writeProductCache(cache) {
    try {
      localStorage.setItem(PRODUCT_CACHE_KEY, JSON.stringify(cache));
    } catch (_) {}
  }

  function getSessionCacheKey(sessionId) {
    return String(sessionId || PENDING_SESSION_KEY);
  }

  function normalizeMessageKey(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function extractProductUrlFromText(text) {
    const s = String(text || '');
    const md = s.match(/\[[^\]]*]\((https?:\/\/[^)\s]+)\)/i);
    if (md && md[1]) return md[1];
    const raw = s.match(/https?:\/\/[^\s)]+/i);
    return raw ? raw[0] : '';
  }

  function cacheProductForMessage(sessionId, message, product) {
    if (!product || !message) return;
    const cache = readProductCache();
    const key = getSessionCacheKey(sessionId);
    const list = Array.isArray(cache[key]) ? cache[key] : [];
    const messageKey = normalizeMessageKey(message);
    list.push({
      message: String(message),
      messageKey,
      productUrl: product.url || extractProductUrlFromText(message),
      product: {
        title: product.title || '',
        url: product.url || '',
        image: product.image || '',
        imageAlt: product.imageAlt || '',
        price: product.price || null,
        type: product.type || null,
        vendor: product.vendor || null,
        authorName: product.authorName || null,
        deliveryStatus: product.deliveryStatus || null,
        available: product.available === false ? false : true
      },
      ts: Date.now()
    });
    cache[key] = list.slice(-MAX_CACHED_PRODUCTS_PER_SESSION);
    writeProductCache(cache);
  }

  function migratePendingProductCache(newSessionId) {
    if (!newSessionId) return;
    const cache = readProductCache();
    const pending = Array.isArray(cache[PENDING_SESSION_KEY]) ? cache[PENDING_SESSION_KEY] : [];
    if (!pending.length) return;
    const key = getSessionCacheKey(newSessionId);
    const existing = Array.isArray(cache[key]) ? cache[key] : [];
    cache[key] = [...existing, ...pending].slice(-MAX_CACHED_PRODUCTS_PER_SESSION);
    delete cache[PENDING_SESSION_KEY];
    writeProductCache(cache);
  }

  function consumeCachedProduct(sessionId, message) {
    if (!message) return null;
    const cache = readProductCache();
    const key = getSessionCacheKey(sessionId);
    const list = Array.isArray(cache[key]) ? cache[key] : [];
    const messageKey = normalizeMessageKey(message);
    const msgUrl = extractProductUrlFromText(message);

    const matchInList = (arr) => {
      if (!Array.isArray(arr)) return -1;
      let idx = arr.findIndex(entry =>
        entry &&
        entry.product &&
        entry.product.url &&
        (entry.messageKey === messageKey || normalizeMessageKey(entry.message) === messageKey)
      );
      if (idx !== -1) return idx;
      if (msgUrl) {
        idx = arr.findIndex(entry =>
          entry &&
          entry.product &&
          entry.product.url &&
          ((entry.productUrl && entry.productUrl === msgUrl) || entry.product.url === msgUrl)
        );
      }
      return idx;
    };

    let idx = matchInList(list);
    let selectedKey = key;

    // Fallback: search across all cached sessions when history has no session_id.
    if (idx === -1) {
      const allKeys = Object.keys(cache);
      for (const k of allKeys) {
        if (k === key) continue;
        const candidateList = Array.isArray(cache[k]) ? cache[k] : [];
        const found = matchInList(candidateList);
        if (found !== -1) {
          idx = found;
          selectedKey = k;
          break;
        }
      }
      if (idx === -1) return null;
    }

    const selectedList = Array.isArray(cache[selectedKey]) ? cache[selectedKey] : [];
    const entry = selectedList[idx];
    return entry?.product || null;
  }

  function injectTriggerGlowStyles() {
    if (document.getElementById('ai-chat-trigger-glow-styles')) return;

    const style = document.createElement('style');
    style.id = 'ai-chat-trigger-glow-styles';
    style.textContent = `
      .ai-chat-glow-target {
        position: relative;
        border-radius: 999px;
        z-index: 1;
      }
      .ai-chat-glow-host {
        position: relative;
        isolation: isolate;
        overflow: visible;
      }
      .ai-chat-input-glow-layer {
        position: absolute;
        left: var(--ai-chat-glow-left, 0px);
        top: var(--ai-chat-glow-top, 0px);
        width: var(--ai-chat-glow-width, 0px);
        height: var(--ai-chat-glow-height, 0px);
        border-radius: 999px;
        pointer-events: none;
        z-index: 0;
        opacity: 0;
        transform: translate(-6px, -6px);
        width: calc(var(--ai-chat-glow-width, 0px) + 12px);
        height: calc(var(--ai-chat-glow-height, 0px) + 12px);
        border: 2px solid transparent;
        --ai-angle: 0deg;
        background:
          linear-gradient(transparent, transparent) padding-box,
          conic-gradient(
            from var(--ai-angle),
            rgba(167, 139, 250, 0.11) 0deg,
            rgba(167, 139, 250, 0.11) 238deg,
            rgba(236, 72, 153, 0.40) 274deg,
            rgba(167, 139, 250, 0.22) 302deg,
            rgba(167, 139, 250, 0.11) 360deg
          ) border-box;
        transition: opacity 0.25s ease;
        will-change: opacity;
        box-shadow:
          0 0 7px rgba(167, 139, 250, 0.14),
          0 0 12px rgba(236, 72, 153, 0.09);
      }
      .ai-chat-glow-target.ai-chat-glow-active {
        box-shadow:
          0 0 0 1px rgba(167, 139, 250, 0.38),
          0 0 0 3px rgba(167, 139, 250, 0.10);
      }
      .ai-chat-glow-target.ai-chat-glow-active + .ai-chat-input-glow-layer {
        opacity: 1;
        animation: aiChatSnake 4.2s linear infinite;
      }
      @property --ai-angle {
        syntax: '<angle>';
        inherits: false;
        initial-value: 0deg;
      }
      @keyframes aiChatSnake {
        0% {
          --ai-angle: 0deg;
        }
        100% {
          --ai-angle: 360deg;
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .ai-chat-glow-target.ai-chat-glow-active + .ai-chat-input-glow-layer {
          animation: none;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function injectInlineLinkStyles() {
    if (document.getElementById('ai-chat-inline-link-styles')) return;
    const style = document.createElement('style');
    style.id = 'ai-chat-inline-link-styles';
    style.textContent = `
      .ai-chat-inline-link {
        color: #2563eb;
        text-decoration: underline;
        font-weight: 600;
        word-break: break-word;
      }
      .ai-chat-inline-link:hover {
        color: #1d4ed8;
      }
    `;
    document.head.appendChild(style);
  }

  function appendBotMessageWithLinks(container, text, product = null) {
    const sanitizeUrl = (value) => {
      let s = String(value || '').trim();
      // Entfernt Satzzeichen am Ende, die häufig nach einem Link stehen.
      s = s.replace(/[.,;:!?]+$/g, '');
      // Falls Klammern unausgeglichen sind, schließende Klammer am Ende entfernen.
      const opens = (s.match(/\(/g) || []).length;
      const closes = (s.match(/\)/g) || []).length;
      if (closes > opens) s = s.replace(/\)+$/g, '');
      return s;
    };

    const normalizeComparableUrl = (value) => {
      try {
        const u = new URL(String(value || ''), window.location.href);
        return `${u.origin}${u.pathname.replace(/\/+$/, '')}`;
      } catch (_) {
        return String(value || '').trim();
      }
    };

    const isDhlTrackingUrl = (value) => {
      try {
        const u = new URL(String(value || ''), window.location.href);
        return /(^|\.)dhl\./i.test(u.hostname) || /\/tracking\/dhl\//i.test(u.pathname) || /sendungsverfolgung|piececode=/i.test(u.href);
      } catch (_) {
        return /sendungsverfolgung|piececode=|\/tracking\/dhl\//i.test(String(value || ''));
      }
    };

    const productTitleFromUrl = (value) => {
      try {
        const u = new URL(String(value || ''), window.location.href);
        const m = u.pathname.match(/\/products\/([^/?#]+)/i);
        const handle = m ? decodeURIComponent(m[1]) : '';
        if (!handle) return isDhlTrackingUrl(value) ? 'DHL-Sendungsverfolgung' : 'Link öffnen';
        return handle
          .replace(/[-_]+/g, ' ')
          .replace(/\b\w/g, (ch) => ch.toUpperCase());
      } catch (_) {
        return isDhlTrackingUrl(value) ? 'DHL-Sendungsverfolgung' : 'Link öffnen';
      }
    };

    const source = String(text || '');
    const re = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|https?:\/\/[^\s)]+/gi;
    let last = 0;
    let match;

    while ((match = re.exec(source)) !== null) {
      if (match.index > last) {
        container.appendChild(document.createTextNode(source.slice(last, match.index)));
      }

      const markdownLabel = match[1];
      const url = sanitizeUrl(match[2] || match[0]);
      if (!url) continue;
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.className = 'ai-chat-inline-link';

      const isGenericLabel = markdownLabel && /^(link|produkt|product|tracking|tracking-link)$/i.test(markdownLabel.trim());
      const sameProductUrl = !!(product?.url) && normalizeComparableUrl(url) === normalizeComparableUrl(product.url);

      if (product?.title && sameProductUrl) {
        anchor.textContent = product.title;
      } else if (markdownLabel) {
        anchor.textContent = isGenericLabel ? productTitleFromUrl(url) : markdownLabel;
      } else {
        anchor.textContent = productTitleFromUrl(url);
      }

      container.appendChild(anchor);
      last = re.lastIndex;
    }

    if (last < source.length) {
      container.appendChild(document.createTextNode(source.slice(last)));
    }
  }

  function injectStreamingStyles() {
    if (document.getElementById('ai-chat-streaming-styles')) return;
    const style = document.createElement('style');
    style.id = 'ai-chat-streaming-styles';
    style.textContent = `
      .bot-bubble.ai-chat-streaming::after {
        content: '';
        display: inline-block;
        width: 0;
        height: 1em;
        margin-left: 2px;
        vertical-align: -0.12em;
        border-right: 2px solid rgba(37, 99, 235, 0.55);
        animation: aiChatCaretBlink 1s steps(1) infinite;
      }
      .chat-product-card.chat-product-card-enter {
        opacity: 0;
        transform: translateY(6px);
      }
      .chat-product-card.chat-product-card-ready {
        opacity: 1;
        transform: translateY(0);
        transition: opacity 0.22s ease, transform 0.22s ease;
      }
      @keyframes aiChatCaretBlink {
        0%, 49% { opacity: 1; }
        50%, 100% { opacity: 0; }
      }
      @media (prefers-reduced-motion: reduce) {
        .bot-bubble.ai-chat-streaming::after {
          animation: none;
        }
        .chat-product-card.chat-product-card-ready {
          transition: none;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function buildStreamingSegments(text, product = null) {
    const sanitizeUrl = (value) => {
      let s = String(value || '').trim();
      s = s.replace(/[.,;:!?]+$/g, '');
      const opens = (s.match(/\(/g) || []).length;
      const closes = (s.match(/\)/g) || []).length;
      if (closes > opens) s = s.replace(/\)+$/g, '');
      return s;
    };

    const normalizeUrl = (value) => {
      try {
        const u = new URL(String(value || ''), window.location.href);
        return `${u.origin}${u.pathname.replace(/\/+$/, '')}`;
      } catch (_) {
        return String(value || '').trim();
      }
    };

    const isDhlUrl = (value) => {
      try {
        const u = new URL(String(value || ''), window.location.href);
        return /(^|\.)dhl\./i.test(u.hostname) || /\/tracking\/dhl\//i.test(u.pathname) || /sendungsverfolgung|piececode=/i.test(u.href);
      } catch (_) {
        return /sendungsverfolgung|piececode=|\/tracking\/dhl\//i.test(String(value || ''));
      }
    };

    const labelFromUrl = (value) => {
      try {
        const u = new URL(String(value || ''), window.location.href);
        const m = u.pathname.match(/\/products\/([^/?#]+)/i);
        const handle = m ? decodeURIComponent(m[1]) : '';
        if (!handle) return isDhlUrl(value) ? 'DHL-Sendungsverfolgung' : 'Link öffnen';
        return handle
          .replace(/[-_]+/g, ' ')
          .replace(/\b\w/g, (ch) => ch.toUpperCase());
      } catch (_) {
        return isDhlUrl(value) ? 'DHL-Sendungsverfolgung' : 'Link öffnen';
      }
    };

    const source = String(text || '');
    const re = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|https?:\/\/[^\s)]+/gi;
    const segments = [];
    let last = 0;
    let match;

    while ((match = re.exec(source)) !== null) {
      if (match.index > last) {
        segments.push({ type: 'text', content: source.slice(last, match.index) });
      }

      const markdownLabel = match[1];
      const url = sanitizeUrl(match[2] || match[0]);
      if (!url) {
        last = re.lastIndex;
        continue;
      }

      const isGenericLabel = markdownLabel && /^(link|produkt|product|tracking|tracking-link)$/i.test(markdownLabel.trim());
      const sameProductUrl = !!(product?.url) && normalizeUrl(url) === normalizeUrl(product.url);
      let label = '';

      if (product?.title && sameProductUrl) {
        label = product.title;
      } else if (markdownLabel) {
        label = isGenericLabel ? labelFromUrl(url) : markdownLabel;
      } else {
        label = labelFromUrl(url);
      }

      segments.push({ type: 'link', url, label });
      last = re.lastIndex;
    }

    if (last < source.length) {
      segments.push({ type: 'text', content: source.slice(last) });
    }

    return segments.filter((segment) => {
      if (segment.type === 'text') return segment.content.length > 0;
      return Boolean(segment.url && segment.label);
    });
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function sleep(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  function getStreamingPlan(segments) {
    const visibleChars = segments.reduce((sum, segment) => sum + (segment.type === 'text' ? segment.content.length : segment.label.length), 0);
    if (prefersReducedMotion() || visibleChars < 20) {
      return { animated: false, tick: 0, charsPerTick: visibleChars || 1 };
    }

    const tick = 18;
    const targetDuration = Math.min(1200, Math.max(320, 220 + (visibleChars * 12)));
    const charsPerTick = Math.max(1, Math.ceil(visibleChars / Math.max(1, Math.round(targetDuration / tick))));
    return { animated: true, tick, charsPerTick };
  }

  function nextChunkLength(text, startIndex, charsPerTick) {
    const maxChars = Math.max(1, charsPerTick);
    let count = 0;
    while ((startIndex + count) < text.length && count < maxChars) {
      const char = text[startIndex + count];
      count += 1;
      if (count >= maxChars) break;
      if (/\s/.test(char) && count >= Math.max(1, Math.floor(maxChars / 2))) break;
    }
    return count;
  }

  async function streamTextIntoNode(node, text, plan) {
    if (!plan.animated) {
      node.textContent = text;
      return;
    }

    let index = 0;
    while (index < text.length) {
      const step = nextChunkLength(text, index, plan.charsPerTick);
      node.textContent += text.slice(index, index + step);
      index += step;
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
      const recentChar = node.textContent.slice(-1);
      const pause = /[.!?]/.test(recentChar) ? 70 : /[,;:]/.test(recentChar) ? 40 : 0;
      await sleep(plan.tick + pause);
    }
  }

  async function animateBotMessage(container, text, product = null) {
    const segments = buildStreamingSegments(text, product);
    const plan = getStreamingPlan(segments);

    if (!plan.animated) {
      appendBotMessageWithLinks(container, text, product);
      return;
    }

    injectStreamingStyles();
    container.classList.add('ai-chat-streaming');

    for (const segment of segments) {
      if (segment.type === 'text') {
        const node = document.createTextNode('');
        container.appendChild(node);
        await streamTextIntoNode(node, segment.content, plan);
        continue;
      }

      const anchor = document.createElement('a');
      anchor.href = segment.url;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.className = 'ai-chat-inline-link';
      anchor.textContent = '';
      container.appendChild(anchor);
      await streamTextIntoNode(anchor, segment.label, plan);
    }

    container.classList.remove('ai-chat-streaming');
  }

  function getGlowTargets() {
    const explicitTarget = document.querySelector('[data-ai-chat-glow-target]');
    if (explicitTarget instanceof HTMLElement) {
      return [explicitTarget];
    }

    // Fallback: highlight only the oval text input.
    if (inputField instanceof HTMLElement) {
      return [inputField];
    }

    return [];
  }

  let glowTargets = [];

  function syncInputGlowGeometry(target) {
    if (!(target instanceof HTMLElement)) return;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;

    const parent = target.parentElement;
    if (!parent) return;

    parent.classList.add('ai-chat-glow-host');

    let layer = target.nextElementSibling;
    if (!(layer instanceof HTMLElement) || !layer.classList.contains('ai-chat-input-glow-layer')) {
      layer = document.createElement('span');
      layer.className = 'ai-chat-input-glow-layer';
      layer.setAttribute('aria-hidden', 'true');
      target.insertAdjacentElement('afterend', layer);
    }

    const parentRect = parent.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const left = targetRect.left - parentRect.left;
    const top = targetRect.top - parentRect.top;

    layer.style.setProperty('--ai-chat-glow-left', `${left}px`);
    layer.style.setProperty('--ai-chat-glow-top', `${top}px`);
    layer.style.setProperty('--ai-chat-glow-width', `${targetRect.width}px`);
    layer.style.setProperty('--ai-chat-glow-height', `${targetRect.height}px`);
  }

  function setupTriggerGlow() {
    injectTriggerGlowStyles();

    glowTargets = getGlowTargets();

    glowTargets.forEach(trigger => {
      if (!trigger) return;

      trigger.classList.add('ai-chat-glow-target');
      syncInputGlowGeometry(trigger);
    });
  }

  function updateTriggerGlowState(isOpen) {
    if (!glowTargets.length) {
      glowTargets = getGlowTargets();
    }

    glowTargets.forEach(trigger => {
      if (!trigger) return;
      syncInputGlowGeometry(trigger);
      trigger.classList.toggle('ai-chat-glow-active', isOpen);
    });
  }

  setupTriggerGlow();
  window.addEventListener('resize', () => {
    glowTargets.forEach(syncInputGlowGeometry);
  });

  function isMobileViewport() {
    return window.matchMedia('(max-width: 900px)').matches;
  }

  function clearMobileChatViewportStyles() {
    if (!chatBox) return;
    chatBox.style.removeProperty('position');
    chatBox.style.removeProperty('left');
    chatBox.style.removeProperty('right');
    chatBox.style.removeProperty('top');
    chatBox.style.removeProperty('bottom');
    chatBox.style.removeProperty('width');
    chatBox.style.removeProperty('height');
    chatBox.style.removeProperty('max-height');

    if (messagesContainer) {
      messagesContainer.style.removeProperty('max-height');
      messagesContainer.style.removeProperty('height');
      messagesContainer.style.removeProperty('overflow-y');
      messagesContainer.style.removeProperty('padding-bottom');
    }
  }

  function syncMobileChatViewport() {
    if (!chatBox || !messagesContainer) return;
    if (!isMobileViewport() || chatBox.style.display !== 'block') {
      clearMobileChatViewportStyles();
      return;
    }

    const vv = window.visualViewport;
    const viewportHeight = Math.max(280, Math.floor(vv ? vv.height : window.innerHeight));
    const viewportTop = Math.max(0, Math.floor(vv ? vv.offsetTop : 0));

    chatBox.style.position = 'fixed';
    chatBox.style.left = '0';
    chatBox.style.right = '0';
    chatBox.style.top = `${viewportTop}px`;
    chatBox.style.bottom = 'auto';
    chatBox.style.width = '100vw';
    chatBox.style.height = `${viewportHeight}px`;
    chatBox.style.maxHeight = `${viewportHeight}px`;

    const header = document.getElementById('ai-chat-header');
    const inputWrap = inputField?.closest('.ai-chat-input-wrapper') || inputField?.parentElement;
    const headerH = header?.offsetHeight || 56;
    const inputH = inputWrap?.offsetHeight || 72;
    const chipsEl = document.getElementById('ai-chat-quick-replies');
    const chipsH = (chipsEl && chipsEl.style.display !== 'none') ? chipsEl.offsetHeight : 0;
    const messagesH = Math.max(120, viewportHeight - headerH - inputH - chipsH - 8);

    messagesContainer.style.height = `${messagesH}px`;
    messagesContainer.style.maxHeight = `${messagesH}px`;
    messagesContainer.style.overflowY = 'auto';
    messagesContainer.style.paddingBottom = '8px';
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  }

  window.addEventListener('orientationchange', () => setTimeout(syncMobileChatViewport, 120));
  window.addEventListener('resize', () => setTimeout(syncMobileChatViewport, 80));
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', syncMobileChatViewport);
    window.visualViewport.addEventListener('scroll', syncMobileChatViewport);
  }
  if (inputField) {
    inputField.addEventListener('focus', () => setTimeout(syncMobileChatViewport, 120));
    inputField.addEventListener('blur', () => setTimeout(syncMobileChatViewport, 120));
  }

  // ═══════════════════════════════════════════════════════════
  // SESSION TTL + QUICK REPLIES
  // Sichtbarer Verlauf laeuft nach Inaktivitaet ab; die Server-Historie
  // (api.cinebuch.de) bleibt fuer das Admin-Panel vollstaendig erhalten.
  // ═══════════════════════════════════════════════════════════
  const quickRepliesContainer = document.getElementById('ai-chat-quick-replies');

  const START_QUICK_REPLIES = [
    { label: 'Buch-Tipp', message: 'Ich suche ein gutes Buch.' },
    { label: 'Film-Tipp für heute Abend', message: 'Ich suche einen guten Film.' },
    { label: 'Versand & Lieferzeit', message: 'Wie lange dauert der Versand?' }
  ];

  const PRODUCT_QUICK_REPLIES = [
    { label: 'Ist es sofort lieferbar?', message: 'Ist es sofort lieferbar?' },
    { label: 'Was kostet der Versand?', message: 'Was kostet der Versand?' },
    { label: 'Wo ist meine Bestellung?', message: 'Wo ist meine Bestellung?' }
  ];

  function renderQuickReplies(list) {
    if (!quickRepliesContainer) return;
    quickRepliesContainer.innerHTML = '';
    (list || []).forEach(item => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'ai-chat-chip';
      chip.textContent = item.label;
      chip.addEventListener('click', () => {
        if (inputField) inputField.value = item.message;
        sendMessage();
      });
      quickRepliesContainer.appendChild(chip);
    });
    quickRepliesContainer.style.display = (list && list.length) ? 'flex' : 'none';
    syncMobileChatViewport();
  }

  function touchChatActivity() {
    try {
      if (CURRENT_SESSION_ID) localStorage.setItem(CHAT_SESSION_KEY, CURRENT_SESSION_ID);
      localStorage.setItem(CHAT_LAST_ACTIVITY_KEY, String(Date.now()));
    } catch (_) {}
  }

  function isChatSessionFresh() {
    try {
      const last = Number(localStorage.getItem(CHAT_LAST_ACTIVITY_KEY) || 0);
      return last > 0 && (Date.now() - last) < CHAT_HISTORY_TTL_MS;
    } catch (_) {
      return false;
    }
  }

  function restoreFreshSessionId() {
    try {
      if (!isChatSessionFresh()) return null;
      return localStorage.getItem(CHAT_SESSION_KEY) || null;
    } catch (_) {
      return null;
    }
  }

  function pruneProductCache() {
    try {
      const cache = readProductCache();
      const cutoff = Date.now() - (48 * 60 * 60 * 1000);
      let changed = false;
      for (const key of Object.keys(cache)) {
        const list = Array.isArray(cache[key]) ? cache[key].filter(e => e && e.ts && e.ts > cutoff) : [];
        if (!list.length) {
          delete cache[key];
          changed = true;
        } else if (list.length !== cache[key].length) {
          cache[key] = list;
          changed = true;
        }
      }
      if (changed) writeProductCache(cache);
    } catch (_) {}
  }

  function showGreeting() {
    messagesContainer.innerHTML = '';
    addMessageToDOM('Hallo! Wie kann ich Ihnen helfen? Fragen Sie mich nach Büchern, Filmen, Hörbüchern oder Ihrer Bestellung.', false, null, false);
  }

  function resetConversationUi() {
    CURRENT_SESSION_ID = null;
    try {
      localStorage.removeItem(CHAT_SESSION_KEY);
      localStorage.removeItem(CHAT_LAST_ACTIVITY_KEY);
    } catch (_) {}
    showGreeting();
    renderQuickReplies(START_QUICK_REPLIES);
  }

  function ensureFreshConversation() {
    if (CURRENT_SESSION_ID && !isChatSessionFresh()) {
      resetConversationUi();
    }
  }

  // ═══════════════════════════════════════════════════════════
  // CHAT HISTORY
  // ═══════════════════════════════════════════════════════════
  async function loadChatHistory() {
    try {
      if (!CURRENT_SESSION_ID) {
        showGreeting();
        renderQuickReplies(START_QUICK_REPLIES);
        return;
      }

      const url = `${API_BASE}/chat/history/${CUSTOMER_ID}?session_id=${encodeURIComponent(CURRENT_SESSION_ID)}`;
      const response = await fetch(url);
      if (response.ok) {
        const history = await response.json();
        messagesContainer.innerHTML = '';

        if (history.length === 0) {
          showGreeting();
        } else {
          history.forEach(msg => {
            const historySessionId = msg.session_id || CURRENT_SESSION_ID;
            const restoredProduct = msg.is_user ? null : consumeCachedProduct(historySessionId, msg.message);
            addMessageToDOM(msg.message, msg.is_user, restoredProduct, false);
          });
        }
        renderQuickReplies(START_QUICK_REPLIES);
      }
    } catch (error) {
      console.error('Chat-Verlauf konnte nicht geladen werden:', error);
    }
  }

  async function saveMessage(message, isUser) {
    try {
      const response = await fetch(`${API_BASE}/chat/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: CUSTOMER_ID,
          session_id: CURRENT_SESSION_ID,
          message: message,
          is_user: isUser
        })
      });

      const data = await response.json();
      if (data.session_id && !CURRENT_SESSION_ID) {
        CURRENT_SESSION_ID = data.session_id;
        migratePendingProductCache(CURRENT_SESSION_ID);
      }
      touchChatActivity();
    } catch (error) {
      console.error('Nachricht konnte nicht gespeichert werden:', error);
    }
  }

  // ═══════════════════════════════════════════════════════════
  // CHAT TOGGLE
  // ═══════════════════════════════════════════════════════════
  function toggleChat() {
    const isClosed = chatBox.style.display === 'none' || !chatBox.style.display;

    if (isClosed) {
      chatBox.style.display = 'block';
      updateTriggerGlowState(true);
      ensureFreshConversation();
      syncMobileChatViewport();

      const drawerMenu = document.getElementById('header-sidebar-menu');
      if (drawerMenu && drawerMenu.hasAttribute('open')) {
        drawerMenu.removeAttribute('open');
      }

      if (window.history && window.history.pushState) {
        window.history.pushState({ chatOpen: true }, '');
      }

      setTimeout(() => {
        inputField.focus();
        syncMobileChatViewport();
      }, 100);
    } else {
      chatBox.style.display = 'none';
      updateTriggerGlowState(false);
      clearMobileChatViewportStyles();
    }
  }

  if (chatTrigger) chatTrigger.addEventListener('click', toggleChat);
  if (chatTriggerMobile) chatTriggerMobile.addEventListener('click', toggleChat);
  if (chatClose) chatClose.addEventListener('click', toggleChat);

  // ═══════════════════════════════════════════════════════════
  // PRODUKTKARTE - Gradient Card Design
  // ═══════════════════════════════════════════════════════════
  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function formatPrice(price) {
    if (!price) return null;
    const num = parseFloat(price);
    if (isNaN(num)) return null;
    return num.toFixed(2).replace('.', ',') + ' €';
  }

  function resolveProductImage(product) {
    const p = product || {};
    const candidates = [
      p.image,
      p.imageUrl,
      p.image_url,
      p.featuredImage?.url,
      p.featured_image?.url,
      p.featured_image,
      Array.isArray(p.images) ? (p.images[0]?.url || p.images[0]?.src || p.images[0]) : null,
      Array.isArray(p.image_urls) ? p.image_urls[0] : null,
      p.thumbnail,
      p.thumb
    ].filter(Boolean);

    const src = String(candidates[0] || '').trim();
    if (!src) return '';
    if (src.startsWith('//')) return `https:${src}`;
    return src;
  }

  const productImageCache = new Map();

  function extractProductHandle(productUrl) {
    try {
      const u = new URL(productUrl, window.location.href);
      const m = u.pathname.match(/\/products\/([^/?#]+)/i);
      return m ? m[1] : null;
    } catch (error) {
      return null;
    }
  }

  function normalizeImageUrl(src) {
    const s = String(src || '').trim();
    if (!s) return '';
    if (s.startsWith('//')) return `https:${s}`;
    return s;
  }

  async function fetchImageFromProductUrl(productUrl) {
    const handle = extractProductHandle(productUrl);
    if (!handle) return '';

    const cacheKey = handle.toLowerCase();
    if (productImageCache.has(cacheKey)) {
      return productImageCache.get(cacheKey);
    }

    try {
      const currentOrigin = window.location.origin;
      const endpoint = `${currentOrigin}/products/${encodeURIComponent(handle)}.js`;
      const response = await fetch(endpoint, { headers: { 'Accept': 'application/json' } });
      let candidate = '';

      if (response.ok) {
        const data = await response.json();
        candidate = normalizeImageUrl(
          data?.featured_image ||
          (Array.isArray(data?.images) ? data.images[0] : '') ||
          (Array.isArray(data?.media) ? (data.media[0]?.src || data.media[0]?.preview_image?.src) : '')
        );
      }

      // Some storefronts block /products/<handle>.js with 401.
      // Fallback: read og:image from product HTML (same-origin only).
      if (!candidate && response.status !== 401 && response.status !== 403) {
        const pageUrl = new URL(productUrl, window.location.href);
        const sameOriginPage = `${currentOrigin}${pageUrl.pathname}`;
        const pageRes = await fetch(sameOriginPage, { headers: { 'Accept': 'text/html' } });
        if (pageRes.ok) {
          const html = await pageRes.text();
          const ogMatch =
            html.match(/<meta[^>]+property=["']og:image["'][^>]+content=["']([^"']+)["']/i) ||
            html.match(/<meta[^>]+content=["']([^"']+)["'][^>]+property=["']og:image["']/i);
          candidate = normalizeImageUrl(ogMatch?.[1] || '');
        }
      }

      productImageCache.set(cacheKey, candidate);
      return candidate;
    } catch (error) {
      productImageCache.set(cacheKey, '');
      return '';
    }
  }

  async function hydrateCardImage(card, product, imgAlt, fallbackSrc = '') {
    if (!card || !product?.url) return;
    if (!card.isConnected) return;
    const existing = card.querySelector('.card-image img');
    if (existing) return;

    const shopifySrc = await fetchImageFromProductUrl(product.url);
    const imgSrc = shopifySrc || normalizeImageUrl(fallbackSrc);
    if (!imgSrc || !card.isConnected) return;

    const imageContainer = card.querySelector('.card-image');
    if (!imageContainer) return;

    imageContainer.innerHTML = `<img src="${escapeHtml(imgSrc)}" alt="${imgAlt}">`;
  }

  // Proaktiver Bestellstatus beim Oeffnen (max. 1x pro Tag pro Bestellung)
  async function maybeShowOrderWelcome() {
    try {
      const res = await fetch('https://bot.cinebuch.de/welcome', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: CUSTOMER_ID })
      });
      if (!res.ok) return;
      const data = await res.json();
      if (!data || !data.reply || !data.order_number) return;
      const capKey = 'cinebuch_welcome_' + String(data.order_number).replace(/[^A-Za-z0-9]/g, '');
      const today = new Date().toISOString().slice(0, 10);
      if (localStorage.getItem(capKey) === today) return;
      localStorage.setItem(capKey, today);
      addMessageToDOM(data.reply, false, null, false);
      renderQuickReplies([
        { label: 'Wo ist meine Bestellung?', message: 'Wo ist meine Bestellung?' },
        { label: 'Tracking-Link', message: 'Haben Sie einen Tracking-Link zu meiner Bestellung?' },
        ...START_QUICK_REPLIES
      ].slice(0, 4));
    } catch (_) {}
  }

  const companionShownFor = new Set();

  async function fetchCompanionSuggestion(product) {
    try {
      const handle = extractProductHandle(product.url);
      if (!handle || companionShownFor.has(handle)) return;
      companionShownFor.add(handle);
      const res = await fetch('https://bot.cinebuch.de/companion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handle: handle, session_id: CURRENT_SESSION_ID })
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data && data.product && data.product.url) {
        addMessageToDOM(data.reply || 'Dazu passt auch dieser Titel:', false, data.product, true);
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    } catch (_) {}
  }

  async function addProductToCart(product, button) {
    if (!button || button.dataset.busy === '1') return;
    const handle = extractProductHandle(product.url);
    if (!handle) {
      window.open(product.url, '_blank', 'noopener');
      return;
    }

    const label = button.querySelector('span');
    const originalText = label ? label.textContent : '';
    button.dataset.busy = '1';
    button.classList.add('is-loading');
    if (label) label.textContent = 'Wird hinzugefügt ...';

    try {
      const productRes = await fetch(`/products/${encodeURIComponent(handle)}.js`, { headers: { 'Accept': 'application/json' } });
      if (!productRes.ok) throw new Error('Produktdaten nicht verfügbar');
      const productData = await productRes.json();
      const variants = Array.isArray(productData?.variants) ? productData.variants : [];
      const variant = variants.find(v => v && v.available);
      if (!variant?.id) {
        button.classList.remove('is-loading');
        button.classList.add('is-soldout');
        button.disabled = true;
        if (label) label.textContent = 'Leider ausverkauft';
        delete button.dataset.busy;
        return;
      }

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variant.id, quantity: 1 })
      });
      if (!addRes.ok) throw new Error('Warenkorb-Fehler');

      button.classList.remove('is-loading');
      button.classList.add('is-added');
      if (label) label.textContent = '✓ Im Warenkorb';
      refreshCartIndicators();
      fetchCompanionSuggestion(product);
      setTimeout(() => {
        button.classList.remove('is-added');
        if (label) label.textContent = originalText;
        delete button.dataset.busy;
      }, 2600);
    } catch (error) {
      console.error('Warenkorb-Fehler:', error);
      button.classList.remove('is-loading');
      if (label) label.textContent = 'Fehler – erneut versuchen';
      setTimeout(() => {
        if (label) label.textContent = originalText;
        delete button.dataset.busy;
      }, 2200);
    }
  }

  async function refreshCartIndicators() {
    try {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (_) {}
    try {
      const cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => (r.ok ? r.json() : null));
      if (!cart) return;
      document.querySelectorAll('cart-count, .cart-count, [data-cart-count]').forEach(el => {
        el.textContent = String(cart.item_count);
      });
    } catch (_) {}
  }

  function createProductCard(product) {
    if (!product || !product.url) return null;

    const card = document.createElement('div');
    card.className = 'chat-product-card';

    const priceText = formatPrice(product.price);
    const fallbackImgSrc = resolveProductImage(product);
    const imgSrc = normalizeImageUrl(fallbackImgSrc);
    const imgAlt = escapeHtml(product.imageAlt || product.title);
    const titleShort = product.title.length > 64 ? product.title.substring(0, 64) + '...' : product.title;

    const metaParts = [];
    if (product.type) metaParts.push(String(product.type));
    const author = product.authorName || product.vendor;
    if (author && String(author).toLowerCase() !== String(product.type || '').toLowerCase()) {
      metaParts.push(String(author));
    }
    const metaText = metaParts.map(part => escapeHtml(part)).join(' &middot; ');

    const isAvailable = product.available !== false;
    const delivery = String(product.deliveryStatus || '').trim() || (isAvailable ? '' : 'Nicht lieferbar');
    const deliveryOk = isAvailable && /sofort|auf lager/i.test(delivery) && !/nicht/i.test(delivery);
    const deliveryClass = (!isAvailable || /nicht|ausverkauft/i.test(delivery)) ? 'is-soldout' : (deliveryOk ? 'is-instock' : 'is-delayed');
    const deliveryHtml = delivery
      ? `<div class="card-delivery ${deliveryClass}"><span class="card-delivery-dot"></span>${escapeHtml(delivery)}</div>`
      : '';

    card.innerHTML = `
      <a class="card-image" href="${escapeHtml(product.url)}" target="_blank" rel="noopener">
        ${imgSrc ? `<img src="${escapeHtml(imgSrc)}" alt="${imgAlt}" loading="lazy">` : '<div class="no-img">Bild</div>'}
      </a>
      <div class="card-body">
        <a class="card-title" href="${escapeHtml(product.url)}" target="_blank" rel="noopener">${escapeHtml(titleShort)}</a>
        ${metaText ? `<div class="card-meta">${metaText}</div>` : ''}
        ${priceText ? `<div class="card-price">${priceText}</div>` : ''}
        ${deliveryHtml}
        <div class="card-actions">
          ${isAvailable ? `<button type="button" class="card-add-to-cart">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>
            <span>In den Warenkorb</span>
          </button>` : `<button type="button" class="card-add-to-cart is-soldout" disabled><span>Leider ausverkauft</span></button>`}
          <a class="card-details-btn" href="${escapeHtml(product.url)}" target="_blank" rel="noopener">Details ansehen</a>
        </div>
      </div>
    `;

    const addBtn = card.querySelector('.card-add-to-cart:not(.is-soldout)');
    if (addBtn) {
      addBtn.addEventListener('click', () => addProductToCart(product, addBtn));
    }

    if (!imgSrc) {
      hydrateCardImage(card, product, imgAlt, fallbackImgSrc);
    }

    return card;
  }

  function appendProductCard(messageDiv, product) {
    if (!product) return;
    const productCard = createProductCard(product);
    if (!productCard) return;
    injectStreamingStyles();
    productCard.classList.add('chat-product-card-enter');
    messageDiv.appendChild(productCard);
    requestAnimationFrame(() => {
      productCard.classList.add('chat-product-card-ready');
    });
  }

  // ═══════════════════════════════════════════════════════════
  // NACHRICHT ZUM DOM
  // ═══════════════════════════════════════════════════════════
  function addMessageToDOM(text, isUser, product = null, save = true) {
    const messageDiv = document.createElement('div');
    messageDiv.className = isUser ? 'ai-user-message' : 'ai-bot-message';

    // Bot mesajlarini alt alta gostermek icin style
    if (!isUser && product) {
      messageDiv.style.cssText = 'display: flex; flex-direction: column; align-items: flex-start;';
    }

    const bubble = document.createElement('div');
    bubble.className = isUser ? 'message-bubble user-bubble' : 'message-bubble bot-bubble';
    if (isUser) {
      bubble.textContent = text;
    } else {
      injectInlineLinkStyles();
      injectStreamingStyles();
    }

    messageDiv.appendChild(bubble);

    messagesContainer.appendChild(messageDiv);
    messagesContainer.scrollTop = messagesContainer.scrollHeight;

    if (!isUser) {
      if (save) {
        void animateBotMessage(bubble, text, product).then(() => {
          appendProductCard(messageDiv, product);
          appendFeedbackRow(messageDiv, text);
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        });
      } else {
        appendBotMessageWithLinks(bubble, text, product);
        appendProductCard(messageDiv, product);
      }
    }

    if (save) {
      saveMessage(text, isUser);
      if (!isUser && product) {
        cacheProductForMessage(CURRENT_SESSION_ID, text, product);
      }
    }
  }

  // ═══════════════════════════════════════════════════════════
  // FEEDBACK (👍/👎) — R11 Phase 7
  // ═══════════════════════════════════════════════════════════
  function sendFeedback(vote, replyText, row) {
    try {
      fetch('https://bot.cinebuch.de/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vote: vote,
          session_id: CURRENT_SESSION_ID || '',
          reply_preview: String(replyText || '').slice(0, 300)
        })
      }).catch(function () {});
    } catch (e) { /* Feedback darf nie stoeren */ }
    if (row) {
      row.innerHTML = '<span class="ai-feedback-thanks">Danke für Ihr Feedback!</span>';
      setTimeout(function () { row.remove(); }, 2500);
    }
  }

  function appendFeedbackRow(messageDiv, replyText) {
    if (!document.getElementById('ai-feedback-styles')) {
      const style = document.createElement('style');
      style.id = 'ai-feedback-styles';
      style.textContent = '.ai-feedback-row{display:flex;gap:6px;margin:4px 0 0 6px;}' +
        '.ai-feedback-row button{background:none;border:1px solid #ddd;border-radius:12px;' +
        'padding:2px 8px;font-size:13px;line-height:1;cursor:pointer;opacity:.55;' +
        'transition:opacity .15s,transform .1s;}' +
        '.ai-feedback-row button:hover{opacity:1;transform:scale(1.08);}' +
        '.ai-feedback-thanks{font-size:11px;color:#888;padding:2px 4px;}';
      document.head.appendChild(style);
    }
    const row = document.createElement('div');
    row.className = 'ai-feedback-row';
    const up = document.createElement('button');
    up.type = 'button';
    up.textContent = '👍';
    up.setAttribute('aria-label', 'Hilfreiche Antwort');
    up.onclick = function () { sendFeedback('up', replyText, row); };
    const down = document.createElement('button');
    down.type = 'button';
    down.textContent = '👎';
    down.setAttribute('aria-label', 'Nicht hilfreiche Antwort');
    down.onclick = function () { sendFeedback('down', replyText, row); };
    row.appendChild(up);
    row.appendChild(down);
    messageDiv.appendChild(row);
  }

  // ═══════════════════════════════════════════════════════════
  // TIPP-INDIKATOR
  // ═══════════════════════════════════════════════════════════
  function showTypingIndicator() {
    const typingDiv = document.createElement('div');
    typingDiv.className = 'ai-bot-message';
    typingDiv.id = 'typing-indicator';
    typingDiv.innerHTML = `
      <div class="message-bubble bot-bubble typing-bubble">
        <span class="dot"></span>
        <span class="dot"></span>
        <span class="dot"></span>
      </div>
    `;

    if (!document.getElementById('typing-styles')) {
      const style = document.createElement('style');
      style.id = 'typing-styles';
      style.textContent = `
        .typing-bubble {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 14px 18px !important;
        }
        .typing-bubble .dot {
          width: 8px;
          height: 8px;
          background: #999;
          border-radius: 50%;
          animation: bounce 1.4s infinite ease-in-out both;
        }
        .typing-bubble .dot:nth-child(1) { animation-delay: 0s; }
        .typing-bubble .dot:nth-child(2) { animation-delay: 0.2s; }
        .typing-bubble .dot:nth-child(3) { animation-delay: 0.4s; }
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
      `;
      document.head.appendChild(style);
    }

    messagesContainer.appendChild(typingDiv);
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  }

  function hideTypingIndicator() {
    const typing = document.getElementById('typing-indicator');
    if (typing) typing.remove();
  }

  function createClientSessionId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return `chat_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  // ═══════════════════════════════════════════════════════════
  // NACHRICHT SENDEN
  // ═══════════════════════════════════════════════════════════
  async function sendMessage() {
    const message = inputField.value.trim();
    if (!message) return;

    ensureFreshConversation();
    if (!CURRENT_SESSION_ID) {
      CURRENT_SESSION_ID = createClientSessionId();
    }
    touchChatActivity();

    addMessageToDOM(message, true, null, true);
    inputField.value = '';
    renderQuickReplies([]);
    showTypingIndicator();

    try {
      const response = await fetch('https://bot.cinebuch.de/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: message,
          customer_id: CUSTOMER_ID,
          session_id: CURRENT_SESSION_ID
        })
      });

      if (!response.ok) throw new Error('Netzwerkfehler');

      const data = await response.json();
      hideTypingIndicator();

      if (data.session_id && data.session_id !== CURRENT_SESSION_ID) {
        CURRENT_SESSION_ID = data.session_id;
        migratePendingProductCache(CURRENT_SESSION_ID);
      }

      const replyText = data.reply || 'Entschuldigung, ich konnte keine Antwort generieren.';
      const product = data.product || null;

      addMessageToDOM(replyText, false, product, true);
      const serverChips = Array.isArray(data.suggested_replies)
        ? data.suggested_replies.filter(s => typeof s === 'string' && s.trim()).slice(0, 6).map(s => ({ label: s, message: s }))
        : [];
      renderQuickReplies(serverChips.length ? serverChips : (product ? PRODUCT_QUICK_REPLIES : START_QUICK_REPLIES));

    } catch (error) {
      console.error('Chat-Fehler:', error);
      hideTypingIndicator();
      addMessageToDOM('Der Chat ist momentan nicht verfügbar. Bitte versuchen Sie es später erneut.', false, null, true);
      renderQuickReplies(START_QUICK_REPLIES);
    }
  }

  if (sendButton) {
    sendButton.addEventListener('click', sendMessage);
  }

  if (inputField) {
    inputField.addEventListener('keypress', function(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        sendMessage();
      }
    });
  }

  // ═══════════════════════════════════════════════════════════
  // BROWSER NAVIGATION
  // ═══════════════════════════════════════════════════════════
  window.addEventListener('popstate', function(event) {
    if (chatBox.style.display === 'block') {
      chatBox.style.display = 'none';
      updateTriggerGlowState(false);
    }
  });

  pruneProductCache();
  CURRENT_SESSION_ID = restoreFreshSessionId();
  void loadChatHistory().then(() => maybeShowOrderWelcome());
})();
